/**
 * Set D W2 N2 — stale-bundle nudge DECISION logic.
 *
 * Deliberately dependency-free (no React, no i18n, no lib/api): the frontend
 * test runner has no jsdom, and importing the component would drag in
 * i18n/index.ts which touches localStorage at module scope. Keeping the
 * decision here means the part that can actually be WRONG — when do we nag,
 * when do we stay quiet — is unit-tested for real.
 */
export const SUPPRESS_KEY = 'sc_version_nudge_suppressed_until';
export const HOUR_MS = 60 * 60 * 1000;
export const POLL_MS = 5 * 60 * 1000;

/**
 * Should the nudge appear?
 *  - inside the suppression window (dismissed) → no
 *  - no hashed bundle (dev server) → no
 *  - endpoint gave nothing usable → no (never nag on a failure)
 *  - otherwise: only when the served bundle differs from the running one
 */
export function shouldNudge(a: {
  running: string | null;
  served: string | null | undefined;
  suppressedUntil: number;
  now: number;
}): boolean {
  if (a.now < a.suppressedUntil) return false;
  if (!a.running) return false;
  if (!a.served) return false;
  return a.served !== a.running;
}

/** Dismissal suppresses for exactly one hour from now. */
export function suppressionDeadline(now: number): number { return now + HOUR_MS; }

/** The bundle THIS page is running, read from its own script tag. */
export function runningBundle(): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('script[src*="/assets/index-"]') as HTMLScriptElement | null;
  const m = el?.src.match(/(index-[A-Za-z0-9_-]+\.js)/);
  return m ? m[1] : null;
}

export function readSuppressedUntil(): number {
  try {
    const until = Number(localStorage.getItem(SUPPRESS_KEY) ?? '0');
    return Number.isFinite(until) ? until : 0;
  } catch { return 0; }
}

export function suppressForAnHour(): void {
  try { localStorage.setItem(SUPPRESS_KEY, String(suppressionDeadline(Date.now()))); } catch { /* private mode */ }
}
